import { ErrorLike } from "@apollo/client"
import { useMutation, useQuery } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { gql } from "~/__generated__"
import { useFormErrorHandling } from "~/common/error-handling"
import { podsPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { ResponsiveImage } from "~/content/responsive-image"
import { trackFormError, trackFormStart, trackSessionSignup } from "~/lib/analytics"
import { BrandBand } from "~/marketing/brand-band"
import { Button } from "~/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { TextField } from "~/ui/forms/fields/text-field"
import { FormGeneralErrors } from "~/ui/forms/form-general-errors"
import { GraphqlError } from "~/ui/graphql-error"
import { Textarea } from "~/ui/textarea"

const scheduleQuery = gql(/* GraphQL */ `
  query SessionSchedule {
    nextSessionAt
  }
`)
const signupMutation = gql(/* GraphQL */ `
  mutation CreateSessionSignup($input: SessionSignupCreateInput!) {
    sessionSignupCreate(input: $input) {
      signupId
      idempotent
    }
  }
`)
const schema = z.object({
  name: z.string().trim().min(1, "Enter your name.").max(255),
  email: z.string().trim().email("Enter a valid email address."),
  building: z.string().trim().max(10_000),
})

const formatSchedule = (date: string | null | undefined) => {
  if (!date) return "Next date announced soon · 60 minutes · Zoom"
  const value = new Date(date)
  const weekday = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: "America/New_York",
  }).format(value)
  const monthDay = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "America/New_York",
  }).format(value)
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
  })
    .format(value)
    .toLowerCase()
  return `${weekday}, ${monthDay} · ${time} ET · 60 minutes · Zoom`
}

export const SessionsPage = () => {
  useDocumentTitle("Free sessions | Tanooki Labs")
  const { data } = useQuery(scheduleQuery)
  const [submit, { error }] = useMutation(signupMutation)
  const [success, setSuccess] = useState(false)
  const started = useRef(false)
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", building: "" },
  })
  const { onError } = useFormErrorHandling(form)
  const nextSessionAt = data?.nextSessionAt
  const onFocusCapture = () => {
    if (!started.current) {
      trackFormStart("session")
      started.current = true
    }
  }
  const onSubmit = async (values: z.infer<typeof schema>) => {
    try {
      const params = new URLSearchParams(window.location.search)
      const result = await submit({
        variables: {
          input: {
            sessionSignupInput: {
              ...values,
              building: values.building || undefined,
              utmSource: params.get("utm_source") || undefined,
              utmMedium: params.get("utm_medium") || undefined,
              utmCampaign: params.get("utm_campaign") || undefined,
              referrer: document.referrer || undefined,
            },
          },
        },
      })
      if (result.error) return onError(result.error)
      setSuccess(true)
      trackSessionSignup()
      form.reset()
    } catch (requestError) {
      onError(
        requestError instanceof Error
          ? requestError
          : new Error("We couldn't save your seat. Please try again.")
      )
    }
  }
  const disabled = form.formState.isSubmitting
  return (
    <>
      <section className="container marketing-hero grid gap-10 md:grid-cols-2 md:items-start lg:gap-20">
        <div className="max-w-xl">
          <p className="marketing-eyebrow marketing-orange-text">Free monthly session</p>
          <h1 className="home-sans-heading marketing-page-title marketing-split-title mt-4">
            Vibe code <span className="home-emphasis home-highlight">for real.</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
            A free, live Zoom with Tanooki’s senior engineers. Bring your idea or your half-built
            app, and leave knowing how to ship it.
          </p>
          <p className="mt-6 text-sm text-muted-foreground">
            10–20 people · Hosted by Dave Renz and Eric Skiff
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-9">
          <p className="marketing-eyebrow marketing-orange-text">Next session</p>
          <h2 className="home-sans-heading mt-3 text-xl font-semibold">
            {formatSchedule(nextSessionAt)}
          </h2>
          {success ? (
            <p className="mt-8 text-lg leading-relaxed">
              You’re on the list. Check your inbox for the details.
            </p>
          ) : (
            <Form {...form}>
              <form
                className="mt-7 space-y-5"
                onSubmit={form.handleSubmit(onSubmit, (errors) => {
                  const fields = Object.keys(errors)
                  if (fields[0]) trackFormError("session", fields[0], fields.length)
                })}
                onFocusCapture={onFocusCapture}
                noValidate
              >
                <FormGeneralErrors control={form.control} />
                {error && <GraphqlError error={error as ErrorLike} />}
                <TextField
                  control={form.control}
                  name="name"
                  label="Name"
                  required
                  disabled={disabled}
                />
                <TextField
                  control={form.control}
                  name="email"
                  label="Email"
                  type="email"
                  required
                  disabled={disabled}
                />
                <FormField
                  control={form.control}
                  name="building"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>What are you building? (optional)</FormLabel>
                      <FormControl>
                        <Textarea {...field} disabled={disabled} rows={4} />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full rounded-full text-lg" disabled={disabled}>
                  {nextSessionAt ? "Save my seat →" : "Get the next date →"}
                </Button>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Free. We’ll also add you to the newsletter; unsubscribe anytime.
                </p>
              </form>
            </Form>
          )}
        </div>
      </section>
      <BrandBand />
      <section className="container marketing-section">
        <p className="marketing-eyebrow marketing-orange-text">What you’ll get</p>
        <h2 className="home-sans-heading marketing-section-heading mt-4">
          <span className="home-emphasis">An hour</span> that saves you a month.
        </h2>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [
              "Ship, not just prototype",
              "How to get an AI-built app from “works on my laptop” to production.",
            ],
            ["Work like we do", "Our real workflow: transcripts to tickets to agents to review."],
            ["Avoid the traps", "Security, cost and code-quality mistakes we see every week."],
            ["Ask anything", "Bring your app or idea. We’ll look at it live."],
          ].map(([title, body]) => (
            <div key={title} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="text-xl font-semibold tracking-[-0.01em] sm:text-2xl">{title}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="marketing-surface-dark marketing-section text-secondary-foreground">
        <div className="container grid gap-12 md:grid-cols-2 md:gap-20">
          <div>
            <p className="marketing-eyebrow text-primary">Your hosts</p>
            <h2 className="home-sans-heading marketing-section-heading mt-4">
              <span className="home-emphasis">Two founders</span> who build with AI every day.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-secondary-foreground/75">
              Dave and Eric have run Tanooki Labs for 12+ years and 200+ product launches. Now they
              build with agents daily, and they’ll show you how.
            </p>
          </div>
          <div className="grid gap-6">
            <Host assetId="group-848" name="Dave Renz" />
            <Host assetId="skiff-avatar-site3" name="Eric Skiff" />
          </div>
        </div>
      </section>
      <section className="marketing-section bg-primary text-primary-foreground">
        <div className="container grid gap-8 md:grid-cols-2 md:items-center">
          <div>
            <p className="marketing-eyebrow">Ready to build?</p>
            <h2 className="home-sans-heading marketing-section-heading mt-4">
              <span className="home-emphasis">Skip ahead</span> to a pod.
            </h2>
            <p className="mt-5 text-lg leading-relaxed">
              Senior people, AI agents, billed by the month. Start at Low for $5k.
            </p>
          </div>
          <a
            href={podsPath({})}
            className="inline-flex min-h-12 w-fit items-center rounded-full bg-background px-6 text-lg font-semibold text-foreground"
          >
            See the pods →
          </a>
        </div>
      </section>
    </>
  )
}
const Host = ({ assetId, name }: { assetId: "group-848" | "skiff-avatar-site3"; name: string }) => (
  <div className="flex items-center gap-5">
    <ResponsiveImage
      assetId={assetId}
      alt={name}
      sizes="avatar"
      className="size-20 rounded-full object-cover"
    />
    <div>
      <h3 className="text-xl font-semibold tracking-[-0.01em] sm:text-2xl">{name}</h3>
      <p className="mt-1 text-secondary-foreground/75">Co-founder</p>
    </div>
  </div>
)
